import React, { useState, useEffect } from 'react';
import { useParams, useNavigate} from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useCart } from '../../contexts/CartContext';
import { analyzeAndSanitize } from '../../utils/sanitizeHtml';
import { useLanguage } from '../../contexts/LanguageContext';
import { t } from '../../translations';
import { formatEventDateRange } from '../../utils/eventDates';
import './EventDetailPage.css';
import ShareButtons from '../ShareButtons';

function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null); // Track selected image - start with null
  const [isLandscape, setIsLandscape] = useState(false); // Track if current image is landscape
  const { isMember } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventDoc = await getDoc(doc(db, 'events', id));
        if (eventDoc.exists()) {
          const eventData = { id: eventDoc.id, ...eventDoc.data() };
          setEvent(eventData);
          // Set the initial selected image to the mainImageIndex if it exists
          if (eventData.mainImageIndex !== undefined && eventData.mainImageIndex !== null) {
            setSelectedImageIndex(eventData.mainImageIndex);
          } else {
            setSelectedImageIndex(0); // Default to first image if no mainImageIndex
          }
        } else {
          setError('Event not found');
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEvent();
    }
  }, [id]);

  // Check if the selected image is landscape
  const checkImageOrientation = (imageUrl) => {
    if (!imageUrl) return;
    
    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      // Consider landscape if width is significantly greater than height (aspect ratio > 1.2)
      setIsLandscape(aspectRatio > 1.2);
    };
    img.src = imageUrl;
  };

  // Check orientation when selected image changes
  useEffect(() => {
    if (!event || selectedImageIndex === null) return;
    
    // Get the current image URL
    let currentImageUrl = null;
    if (event.images && event.images.length > 0) {
      currentImageUrl = event.images[selectedImageIndex];
    } else if (event.imageUrl || event.image) {
      currentImageUrl = event.imageUrl || event.image;
    }
    
    if (currentImageUrl) {
      checkImageOrientation(currentImageUrl);
    }
  }, [selectedImageIndex, event]);

  const formatDate = (date) => {
    if (!date) return '';
    return formatEventDateRange(date, event?.endDate, language === 'sv' ? 'sv-SE' : 'en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return '';
    const eventDate = date.toDate ? date.toDate() : new Date(date);
    return eventDate.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handler for ticket price click
  const handleTicketClick = (e, ticketPrice, ticketLabel) => {
    e.preventDefault();

    const eventDate = event.date && event.date.toDate ? event.date.toDate() : new Date(event.date);

    // Create the product object for the cart
    const product = {
      id: `event-${event.id}${ticketLabel ? `-${ticketLabel.replace(/\s+/g, '-').toLowerCase()}` : ''}`,
      name: ticketLabel ? `${event.title} (${ticketLabel})` : event.title,
      price: ticketPrice,
      memberPrice: event.price2 ? undefined : event.memberPrice,
      type: 'event',
      eventId: event.id,
      eventDate: eventDate,
      formattedDate: formatEventDateRange(eventDate, event.endDate, 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }),
      formattedTime: eventDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      image: event.thumbnailUrl || event.imageUrl || '/images/studio.png',
      category: 'Event',
      isEvent: true
    };

    // Add to cart
    addToCart(product);

    // Remember where the user came from so "Continue Shopping" can return here
    try {
      sessionStorage.setItem('lastShoppingPath', window.location.pathname);
    } catch (_) {}

    // Navigate to cart page
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="event-detail-page">
        <div className="loading">Loading event...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-page">
        <div className="error">
          <h2>Event Not Found</h2>
          <p>{error || 'The event you are looking for does not exist.'}</p>
          <button onClick={() => navigate(-1)} className="btn btn--outline">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Get all event images
  const getEventImages = () => {
    // Return the images array directly - thumbnails are stored separately
    if (event && event.images && event.images.length > 0) {
      return event.images;
    } else if (event && (event.imageUrl || event.image)) {
      // Fallback to single image if no images array
      return [event.imageUrl || event.image];
    }
    return [];
  };

  const eventImages = getEventImages();

  return (
    <div className="event-detail-page">
      {/* Halftone band: poster + thumbs + right-side info (title, tickets, date/time) */}
      <div className="halftone-band">
        <div className={`event-detail-columns ${isLandscape ? 'landscape-image' : ''}`}>
          {/* Left Column - Image & Thumbnails (NO description in this band) */}
          <div className="event-detail-column">
            {eventImages.length > 0 && selectedImageIndex !== null && (
              <div className="event-main-image">
                <img 
                  src={eventImages[selectedImageIndex] || eventImages[0]} 
                  alt={`${event.title} - Image ${selectedImageIndex + 1}`} 
                />
              </div>
            )}

            {eventImages.length > 1 && (
              <div className="event-thumbnail-gallery">
                {eventImages.map((image, index) => (
                  <div 
                    key={index}
                    className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img 
                      src={image} 
                      alt={`${event.title} thumbnail ${index + 1}`} 
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Event Info (title, tickets, date/time/location) */}
          <div className="event-detail-column">
            <div className="event-info-panel">
              <h1 className="event-title">{(language === 'sv' && event.title_sv) || event.title}</h1>

              {(event.subtitle || event.subtitle_sv) && (
                <p className="event-subtitle">{(language === 'sv' && event.subtitle_sv) || event.subtitle}</p>
              )}

              <div className="event-datetime">
                <div className="event-date">{formatDate(event.date)}</div>
                <div className="event-time">{t('eventDetail.doors', language)}: {formatTime(event.date)}</div>
                {event.location && (
                  <div className="event-location">{t('eventDetail.location', language)}: {event.location}</div>
                )}
              </div>

              {(() => {
                const tiers = [
                  { price: event.price, label: event.priceLabel, label_sv: event.priceLabel_sv },
                  { price: event.price2, label: event.price2Label, label_sv: event.price2Label_sv },
                  { price: event.price3, label: event.price3Label, label_sv: event.price3Label_sv },
                  { price: event.price4, label: event.price4Label, label_sv: event.price4Label_sv },
                  { price: event.price5, label: event.price5Label, label_sv: event.price5Label_sv },
                ].filter(tier => tier.price > 0);

                if (tiers.length === 0) return null;

                if (tiers.length === 1) {
                  const tier = tiers[0];
                  return (
                    <div className="event-tickets">
                      <a
                        href="#"
                        onClick={(e) => handleTicketClick(e, tier.price, tier.label)}
                        className="ticket-button highlighted"
                      >
                        {t('eventDetail.tickets', language)}: {tier.price} kr
                      </a>
                    </div>
                  );
                }

                return (
                  <div className="event-ticket-tiers">
                    {tiers.map((tier, i) => (
                      <div className="ticket-tier-row" key={i}>
                        <span className="ticket-tier-label">{(language === 'sv' && tier.label_sv) || tier.label || `Option ${i + 1}`}</span>
                        <a
                          href="#"
                          onClick={(e) => handleTicketClick(e, tier.price, tier.label)}
                          className="ticket-button highlighted"
                        >
                          {t('eventDetail.tickets', language)}: {tier.price} kr
                        </a>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {(event.menuText || event.menuText_sv) && (
                <div className="event-menu-box">
                  <h3 className="event-menu-heading">{(language === 'sv' && event.menuHeading_sv) || event.menuHeading || t('eventDetail.menu', language)}</h3>
                  <div
                    className="event-menu-content"
                    dangerouslySetInnerHTML={{ __html: analyzeAndSanitize((language === 'sv' && event.menuText_sv) || event.menuText).html }}
                  />
                </div>
              )}

              <ShareButtons
                url={`https://ideafarm.se/event/${event.id}`}
                title={(language === 'sv' && event.title_sv) || event.title}
                description={(language === 'sv' && event.subtitle_sv) || event.subtitle || ''}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Description section with grey/green background */}
      <div className="event-description-section">
        <div className="event-description-container">
          <div className="event-description-text">
            {(event.richDescription || event.richDescription_sv) ? (
              <div
                className="rte-display"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const content = (language === 'sv' && event.richDescription_sv) || event.richDescription;
                    const { html } = analyzeAndSanitize(content);
                    return html;
                  })()
                }}
              />
            ) : (
              <p>{event.description || 'No description available for this event.'}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetailPage;
